import React, { useEffect, useState } from 'react';
import { holidayAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const TYPE_CFG = {
  PUBLIC:     { bg: '#fee2e2', color: '#991b1b', dot: '#ef4444', label: 'Public' },
  OPTIONAL:   { bg: '#fef3c7', color: '#92400e', dot: '#f59e0b', label: 'Optional' },
  RESTRICTED: { bg: '#d1fae5', color: '#065f46', dot: '#10b981', label: 'Restricted' },
};

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const emptyForm = { name: '', date: '', type: 'PUBLIC', description: '' };

export default function Holidays() {
  const { isAdmin } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, [year]);

  const load = () => {
    setLoading(true);
    holidayAPI.getByYear(year)
      .then(r => { setHolidays(r.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  const openAdd = () => { setEditing(null); setForm(emptyForm); setModal(true); };
  const openEdit = h => { setEditing(h); setForm({ name: h.name, date: h.date, type: h.type, description: h.description || '' }); setModal(true); };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editing) { await holidayAPI.update(editing.id, { ...form, year }); toast.success('Holiday updated!'); }
      else { await holidayAPI.create({ ...form, year }); toast.success('Holiday added!'); }
      setModal(false); load();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this holiday?')) return;
    try { await holidayAPI.delete(id); toast.success('Deleted!'); load(); }
    catch { toast.error('Failed to delete'); }
  };

  const grouped = MONTHS.reduce((acc, m, i) => {
    acc[i + 1] = holidays.filter(h => new Date(h.date).getMonth() === i);
    return acc;
  }, {});

  const today = new Date();
  today.setHours(0,0,0,0);
  const upcoming = holidays.filter(h => new Date(h.date) >= today).slice(0, 4);

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Holiday Calendar</div>
          <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 2 }}>{holidays.length} holidays in {year}</div>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <select value={year} onChange={e => setYear(Number(e.target.value))} style={{ padding: '9px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, background: '#fff', cursor: 'pointer' }}>
            {[2024,2025,2026,2027].map(y => <option key={y}>{y}</option>)}
          </select>
          {isAdmin() && (
            <button onClick={openAdd} style={{ padding: '10px 22px', background: 'linear-gradient(135deg, #1a237e, #1565c0)', color: 'white', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 13, boxShadow: '0 4px 14px rgba(26,35,126,0.3)' }}>
              + Add Holiday
            </button>
          )}
        </div>
      </div>

      {/* Legend + Upcoming */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
        <div style={{ background: '#fff', borderRadius: 14, padding: '14px 22px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
          {Object.entries(TYPE_CFG).map(([type, cfg]) => (
            <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: cfg.dot }} />
              <span style={{ fontSize: 12, color: '#475569', fontWeight: 600 }}>{cfg.label}</span>
              <span style={{ fontSize: 12, background: cfg.bg, color: cfg.color, padding: '1px 8px', borderRadius: 20, fontWeight: 700 }}>{holidays.filter(h => h.type === type).length}</span>
            </div>
          ))}
        </div>

        {upcoming.length > 0 && (
          <div style={{ flex: 1, background: 'linear-gradient(135deg, #0a1628, #1a237e)', borderRadius: 14, padding: '16px 22px', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', right: -20, top: -20, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
            <div style={{ fontSize: 10, fontWeight: 800, color: 'rgba(255,255,255,0.4)', letterSpacing: 1.5, marginBottom: 10 }}>UPCOMING HOLIDAYS</div>
            <div style={{ display: 'flex', gap: 20, position: 'relative' }}>
              {upcoming.map(h => {
                const cfg = TYPE_CFG[h.type] || TYPE_CFG.PUBLIC;
                return (
                  <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
                    <div>
                      <div style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>{h.name}</div>
                      <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>
                        {new Date(h.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Calendar Grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 80, color: '#94a3b8' }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>⏳</div>Loading...
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 16 }}>
          {MONTHS.map((month, idx) => {
            const mHols = grouped[idx + 1] || [];
            const hasHols = mHols.length > 0;
            return (
              <div key={month} style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', opacity: hasHols ? 1 : 0.6, transition: 'box-shadow 0.15s' }}>
                <div style={{ padding: '12px 18px', background: hasHols ? 'linear-gradient(135deg, #1a237e, #1565c0)' : '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 700, fontSize: 14, color: hasHols ? '#fff' : '#94a3b8' }}>{month}</span>
                  <span style={{ fontSize: 11, background: hasHols ? 'rgba(255,255,255,0.15)' : '#e2e8f0', color: hasHols ? '#fff' : '#94a3b8', padding: '2px 10px', borderRadius: 20, fontWeight: 600 }}>
                    {mHols.length} {mHols.length === 1 ? 'holiday' : 'holidays'}
                  </span>
                </div>
                <div style={{ padding: mHols.length > 0 ? '8px 0' : '14px 18px' }}>
                  {mHols.length === 0 ? (
                    <div style={{ fontSize: 12, color: '#cbd5e1', fontStyle: 'italic' }}>No holidays this month</div>
                  ) : mHols.map(h => {
                    const cfg = TYPE_CFG[h.type] || TYPE_CFG.PUBLIC;
                    return (
                      <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 18px', borderBottom: '1px solid #f8fafc', transition: 'background 0.1s' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{h.name}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                            {new Date(h.date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                            <span style={{ marginLeft: 8, background: cfg.bg, color: cfg.color, padding: '1px 7px', borderRadius: 10, fontSize: 10, fontWeight: 700 }}>{h.type}</span>
                          </div>
                        </div>
                        {isAdmin() && (
                          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                            <button onClick={() => openEdit(h)} style={{ background: '#eff6ff', border: 'none', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 10, color: '#2563eb', fontWeight: 700 }}>Edit</button>
                            <button onClick={() => handleDelete(h.id)} style={{ background: '#fff1f2', border: 'none', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 10, color: '#e11d48', fontWeight: 700 }}>Del</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}>
          <div style={{ background: '#fff', borderRadius: 20, width: '90%', maxWidth: 480, boxShadow: '0 32px 80px rgba(0,0,0,0.35)', overflow: 'hidden' }}>
            <div style={{ background: 'linear-gradient(135deg, #0a1628, #1a237e)', padding: '22px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ color: '#fff', fontWeight: 800, fontSize: 18 }}>{editing ? 'Edit Holiday' : 'Add Holiday'}</div>
              <button onClick={() => setModal(false)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: 36, height: 36, borderRadius: '50%', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>
            <form onSubmit={handleSubmit} style={{ padding: 28 }}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6, letterSpacing: 0.5, textTransform: 'uppercase' }}>Holiday Name *</div>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="e.g. Republic Day"
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6, letterSpacing: 0.5, textTransform: 'uppercase' }}>Date *</div>
                  <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} required
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 8, letterSpacing: 0.5, textTransform: 'uppercase' }}>Type</div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {Object.entries(TYPE_CFG).map(([t, cfg]) => (
                      <button type="button" key={t} onClick={() => setForm({ ...form, type: t })} style={{
                        flex: 1, padding: '7px 4px', borderRadius: 8, fontSize: 10, fontWeight: 700, cursor: 'pointer',
                        background: form.type === t ? cfg.dot : cfg.bg, color: form.type === t ? '#fff' : cfg.color,
                        border: `1.5px solid ${form.type === t ? cfg.dot : 'transparent'}`, transition: 'all 0.15s',
                      }}>{cfg.label}</button>
                    ))}
                  </div>
                </div>
              </div>
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6, letterSpacing: 0.5, textTransform: 'uppercase' }}>Description</div>
                <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Optional"
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" onClick={() => setModal(false)} style={{ flex: 1, padding: '11px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
                <button type="submit" disabled={saving} style={{ flex: 2, padding: '11px', borderRadius: 10, background: 'linear-gradient(135deg, #1a237e, #1565c0)', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 700, opacity: saving ? 0.7 : 1, boxShadow: '0 4px 14px rgba(26,35,126,0.3)' }}>
                  {saving ? 'Saving...' : editing ? 'Update' : 'Add Holiday'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
